import type {
  RelatedProductIncrement,
  VipIncrement,
} from "@/increment/increment";

/**
 * 新袜子数据模型
 * @author hcy
 */

import { Font } from "@/sunzi";

/** 产品类型 */
export enum SockType {
  SOCK = "SOCK", // 袜子
  BOXER = "BOXER", // 平角内裤
  KYBOXER = "KY_BOXER", // KY平角内裤
  BOXER435 = "BOXER_435", // 平角内裤 435
  BOXER395Z = "BOXER_395Z", // 平角内裤 395Z
  BOXER015Z = "BOXER_015Z", // 平角内裤 015Z
}

/**
 * 袜子数据
 * @size 尺码信息
 * @value 内部尺码值
 * @bottomImages 底图
 * @connects 坐标关联
 */
export interface SockData {
  id: string;
  value: string;
  size: string;
  bottomImages: SockBottomImage[];
  connects: SockDataConnect[];
}

/**
 * 底图数据
 * @name 底图名称
 * @value 内部颜色值
 * @image 底图数据
 * @variantImage 底图变体
 * @fontColors 字体颜色
 */
export interface SockBottomImage {
  id: string;
  value: string;
  name: string;
  image: string;
  variantImage?: string;
  fontColors?: string[];
}

/**
 * 袜子坐标关联数据
 * @images 图形块
 * @lineText 直线刻字块
 */
export interface SockDataConnect {
  images: SockDataImage[];
  lineTexts?: SockDataLineText[];
}

/**
 * 袜子坐标数据
 * @left 左
 * @top 上
 * @width 宽
 * @height 高
 * @angle 角度
 */
interface SockDataCoord {
  left: number;
  top: number;
  width: number;
  height: number;
  angle: number;
}

/**
 * 图像块数据
 */
export interface SockDataImage extends SockDataCoord {}

/**
 * 人脸数据
 */
export interface FaceRect extends Omit<SockDataCoord, "angle"> {}

/**
 * 直线刻字块数据
 * @fontSize 字体大小
 * @fontColor 字体颜色
 * @fontShadowColor 字体阴影颜色
 * @font 字体
 * @fonts 可选字体
 * @bottomColor 底色
 * @isRequire 必填项
 * @maxLength 最大长度
 */
export interface SockDataLineText extends SockDataCoord {
  fontSize: number;
  fontColor: string;
  fontShadowColor: string;
  font: Font;
  fonts?: Font[];
  bottomColor?: string;
  isRequire?: boolean;
  maxLength: number;
}

/**
 * 用户袜子选择数据
 * @size 用户选择的尺寸
 * @color 用户选择的底图颜色
 */
export interface ProductType {
  size: string | undefined;
  color: string | undefined;
}

/**
 * Canvas 画图数据
 * @bottonImage 底图
 * @connects 坐标关联
 * @images 图像块数据
 * @lineTexts 直线刻字块数据
 */
export interface PatternData {
  bottonImage: string;
  connects: SockDataConnect;
  images: SockDataImage[];
  lineTexts: SockDataLineText[];
}

/**
 * 用户上传数据
 * @image 裁剪后的图片
 * @origin 用户上传的原图
 * @faceRect 人脸识别
 */
export interface ImageData {
  image: string;
  origin: string;
  faceRect?: FaceRect;
}

/**
 * 底图 base64 缓存
 * @propName 尺寸 id
 * |_
 *    @size 尺寸名称
 *    @proName 底图 id
 *    |_
 *       @id 底图 id
 *       @name 底图名称
 *       @imageUrl 底图 url
 *       @image 底图 base 64
 *       @variantImage 选择颜色 url
 *       @fontColors 字体颜色值
 */
export interface CacheImageData {
  [propName: string]: {
    value: string;
    size: string;
    [propName: string]:
      | {
          id: string;
          value: string;
          name: string;
          imageUrl: string;
          image: string;
          variantImage?: string;
          fontColors?: string[];
        }
      | any;
  };
}

/**
 * 加车输出图片
 * @origin 用户上传原始图片
 * @avatar 扣头后图片
 */
export interface ConfirmOutputImage {
  origin: string;
  avatar: string;
}

/**
 * 加车输出项
 * @bottomImage 底图 base64
 * @connects 选择的坐标信息
 * @size 选择的尺寸
 * @preview 模型预览
 */
export interface ConfirmOption {
  bottomImage: SockBottomImage;
  connects: SockDataConnect;
  size: {
    id: string;
    name: string;
    value: string;
  };
  preview: string;
}

/**
 * 增值服务输出数据
 * @vip vip服务
 * @relatedProduct 关联产品
 * @doubleDesign 二份折扣
 */
export interface IIncrementProduct {
  relatedProduct?: RelatedProductIncrement;
  doubleDesign?: boolean;
  vip?: VipIncrement;
}

/**
 * 定制输出数据
 * @source 原图
 * @data 数据
 * @related 关联产品
 */
export interface ICustomOutput {
  source: ConfirmOutputImage[];
  data: ConfirmOption;
  related?: RelatedProductIncrement;
}

/**
 * 确认输出数据
 * @data 数据
 * @fontColor 字体颜色
 * @font 字体
 */
export interface IConfirmOutput {
  data?: IIncrementProduct;
  fontColor?: string;
  font?: Font;
  isUpgradeVersion?: boolean;
}

/**
 * 材质升级数据
 * @url 主图链接
 * @originPrice 原价
 * @price 当前价格
 * @title 标题
 */
export interface IUpgradeVersionData {
  url: string;
  originPrice?: number;
  price: number;
  title: string;
}
